import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { Button, Modal } from 'syngenta-react-components'
import checked from '../../assets/icons/circle_check_green.svg'
import unChecked from '../../assets/icons/circle_empty.svg'
import DefaultSalesYearForm from '../../components/ModalForms/DefaultSalesYearForm'
import EditRoleForm from '../../components/ModalForms/EditRoleForm'
import NewAdminForm from '../../components/ModalForms/NewAdminForm'
import NewRoleForm from '../../components/ModalForms/NewRoleForm'
import Warning from '../../components/subComponents/Notifications/Warning'
import TableStatus from '../../components/subComponents/TableStatus/TableStatus'
import { Tab, TabList, TabPanel, Tabs } from '../../components/Tab/Tabs'
import DetailCardWithTable from '../../dpe-usco/components/Details/DetailCardWithTable'
import '../../dpe-usco/components/Table/Table.scss'
import {
  deleteAllAccountLeads,
  getAllPermissions,
  getHighestRole,
  getRolesAndPermissions
} from '../../services/apiHelper'
import { AuthContext, GlobalContext } from '../../services/context'
import { convertToTitleCase } from '../../services/utils'
import AddCompanyProgramsView from '../Admin/AddCompanyPrograms/AddCompanyProgramsView'
import UploadAccountLeads from '../Admin/UploadAccountLeads/UploadAccountLeads'
import UploadCompanyTrainingView from '../Admin/UploadCompanyTraining/UploadCompanyTrainingView'
import UploadCompanyTrainingVideo from '../Admin/UploadCompanyTrainingVideo/UploadCompanyTrainingVideoView'
import UserList from '../Admin/UserList/UserList'

const Admin = () => {
  const { state, trackEvent } = useContext(GlobalContext)
  const [rowData, setRowData] = useState([])
  const [roleName, setRoleName] = useState()
  const [showModal, setShowModal] = useState(false)
  const [getData, setGetData] = useState(true)
  const [modalContent, setModalContent] = useState()
  const { hasAccess } = useContext(AuthContext)
  const [accessState, setAccessState] = useState('LOADING')
  const [uploadLinkedBtn, setUploadLinkedBtn] = useState(false)
  const [isProgramsPermission, setIsProgramsPermission] = useState(false)
  const [showDeleteLeadsBtn, setShowDeleteLeadsBtn] = useState(false)
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false)
  const [isDeletingAccountLeads, setIsDeletingAccountLeads] = useState(false)
  const [deleteAccountLeadsError, setDeleteAccountLeadsError] = useState('')

  const canceled = useRef(false)
  useEffect(() => {

    return () => {
      canceled.current = true
    }
  }, [])

  const handleEditRoleModal = useCallback(
    e => {
      e.preventDefault()
      e.stopPropagation()
      setShowModal(true)
      setModalContent('edit')
      trackEvent('edit_role_clicked', { roleName })
    },
    [trackEvent, roleName]
  )

  useEffect(() => {
    const initRoles = () => {
      getRolesAndPermissions().then(res => {
        getAllPermissions().then(res2 => {
          const comparedData = res.map(role => {
            if (typeof role.permissions != 'string') {
              const permissions = res2?.map(permission => {
                if (role.permissions.includes(permission.permission_code)) {
                  return {
                    display_name: permission.permission_display_name,
                    permission: permission.permission_code,
                    role_id: role.role_id,
                    role_name: role.role_name,
                    role_type: role.role_type,
                    isMatching: true
                  }
                } else {
                  return {
                    display_name: permission.permission_display_name,
                    permission: permission.permission_code,
                    role_id: role.role_id,
                    role_name: role.role_name,
                    role_type: role.role_type,
                    isMatching: false
                  }
                }
              })
              const output = {
                role_name: role.role_name,
                role_type: role.role_type,
                date_created: role.date_created,
                subRowContent: customSubRow(permissions),
                permissionsData: permissions
              }
              return output
            }
            return {
              role_name: role.role_name,
              role_type: role.role_type,
              date_created: role.date_created,
              permissions: role.permissions,
              subRowContent: res2
            }
          })
          const alphabeticalData = [...comparedData]
          alphabeticalData.sort((a, b) => {
            return a.role_name > b.role_name ? 1 : -1
          })
          if (!canceled.current) {
            setRowData(() => {
              return alphabeticalData
            })
          }
        })
      })
    }
    initRoles()
    const customSubRow = options => {
      const roles = options?.map(role => {
        return (
          <div
            data-testid="customSubRow"
            key={role.permission}
            className="df fxdrc aic w200 ml20 mt20"
          >
            {formatCircles(role.isMatching)}
            <span className="tac fz15">{role.display_name}</span>
          </div>
        )
      })
      return [
        <div key="unique">
          <div className="df fxww">{roles}</div>
          <div className="df jcfe">
            <Button
              type="success"
              text="Edit"
              customClassName="mr20"
              clickHandler={handleEditRoleModal}
            />
          </div>
        </div>
      ]
    }
  }, [setRowData, getData, handleEditRoleModal])

  const mapHeaders = headerArray => {
    return headerArray.map(header => {
      return {
        value: header,
        format() {
          return headerCell(header)
        }
      }
    })
  }
  const headerCell = value => {
    return (
      <div key={value} className={`${value} cell`}>
        <div>{value}</div>
      </div>
    )
  }
  const RolesHeaders = ['Role name', 'Role type', 'Date created']

  const clickRole = rowContent => {
    setRoleName(rowContent.role_name)
  }

  const structure = {
    headers: mapHeaders(RolesHeaders),
    dataKeys: ['role_name', 'role_type', 'date_created'],
    rowKey: 'role_name',
    hasSubRow: true,
    hasIcon: true,
    dropdownCallback: clickRole,
    rowFormat: {
      role_name: {
        format: obj => {
          return <div>{convertToTitleCase(obj)}</div>
        }
      }
    }
  }

  const handleNewRoleModal = () => {
    setShowModal(true)
    setModalContent('add_role')
    trackEvent('add_roles_clicked')
  }

  const handleInviteAdmin = () => {
    setShowModal(true)
    setModalContent('invite_admin')
    trackEvent('invite_admin_clicked')
  }

  const handleSalesYear = () => {
    setShowModal(true)
    setModalContent('change_sales_year')
  }

  useEffect(() => {
    if (getData) {
      setGetData(false)
    }
  }, [getData, setGetData])

  const subRowStructure = {
    headers: ['Entire Row'],
    dataKeys: ['entireRow'],
    customContent: true
  }

  const formatCircles = val => {
    if (val === true) {
      return <img className="aifs mb5" alt="checked circle" src={checked} />
    }
    if (val === false) {
      return <img className="aifs mb5" alt="unchecked circle" src={unChecked} />
    }
  }

  const modalDecider = () => {
    switch (modalContent) {
      case 'edit':
        return (
          <EditRoleForm
            handleClose={() => setShowModal(false)}
            handleDataRefresh={() => setGetData(true)}
            permisisonsData={rowData}
            roleName={roleName}
          />
        )
      case 'add_role':
        return (
          <NewRoleForm
            handleClose={() => setShowModal(false)}
            handleDataRefresh={() => setGetData(true)}
            permisisonsData={rowData}
          />
        )
      case 'invite_admin':
        return <NewAdminForm handleCancel={() => setShowModal(false)} />
      case 'change_sales_year':
        return <DefaultSalesYearForm handleClose={() => setShowModal(false)} />
      default:
        return (<></>)
    }
  }

  useEffect(() => {
    setAccessState(hasAccess())
  }, [hasAccess])

  useEffect(() => {
    getHighestRole()?.then(res => {
      const permissions = res?.permissions || []
      setUploadLinkedBtn(permissions.includes('update-EAL-bulk'))
      setIsProgramsPermission(permissions.includes('show-programs'))
      setShowDeleteLeadsBtn(permissions.includes('delete-all-eal'))
    })
  }, [state?.userPermission?.hasChanged])

  const handleDeleteAccountLeadsModel = () => {
    setShowDeleteAccountModal(!showDeleteAccountModal)
    setDeleteAccountLeadsError('')
  }

  const deleteAccountLeadsClicked = async () => {
    setIsDeletingAccountLeads(true)
    const response = await deleteAllAccountLeads(false)
    if (response.remove_all) {
      handleDeleteAccountLeadsModel()
      setIsDeletingAccountLeads(false)
      setDeleteAccountLeadsError('')
    } else {
      const errorMessage = response?.message || 'An unknown error occurred.'
      setDeleteAccountLeadsError(errorMessage)
      setIsDeletingAccountLeads(false)
    }
  }

  return (
    <div>
      {hasAccess() === 'SUCCESS' ? (
        <div className="ml25 mr25">

          <div data-testid="adminTable">
            <Tabs defaultIndex={0}>
              <TabList extra={
                [
                  <div data-testid="setSalesYearButton">
                    <Button
                      text="Set default contract year"
                      type="outline"
                      clickHandler={handleSalesYear}
                    />
                  </div>,
                  <div data-testid="addRolesButton" className='h100%'>
                    <Button
                      text="Add roles"
                      borderColor="#107C10"
                      textColor="#107C10"
                      type="outline"
                      clickHandler={handleNewRoleModal}
                    />
                  </div>,
                  <div data-testid="inviteAdminButton" className='h100%'>
                    <Button
                      text="Invite admin"
                      borderColor="#107C10"
                      textColor="#107C10"
                      type="outline"
                      clickHandler={handleInviteAdmin}
                    />
                  </div>,
                  <div data-testid="deleteAccountLeadsButton" className='h100%'>
                    {showDeleteLeadsBtn &&
                      (<Button
                        text="Delete Account Leads"
                        type="error"
                        clickHandler={handleDeleteAccountLeadsModel}
                      />)
                    }
                  </div>
                ]
              }>
                <Tab index={0}>Users</Tab>
                <Tab index={1}>Roles</Tab>
                {
                  isProgramsPermission &&
                  <Tab index={2}>Programs</Tab>
                }
                {
                  uploadLinkedBtn &&
                  <Tab index={3}>Upload Account Leads</Tab>
                }
                <Tab index={4}>Upload Training Video</Tab>
                <Tab index={5}>Upload Stewardship Training</Tab>
              </TabList>
              <TabPanel index={0}>
                <UserList />
              </TabPanel>
              <TabPanel index={1}>
                <DetailCardWithTable
                  title="Roles"
                  structure={structure}
                  subRowStructure={subRowStructure}
                  data={rowData}
                  button={<div className='df'></div>} // this is just to fix the padding of the widget, a better solution should be found to whole component later
                  dropdownCallback={structure.dropdownCallback}
                />
              </TabPanel>
              {
                isProgramsPermission && (
                  <TabPanel index={2}>
                    <AddCompanyProgramsView />
                  </TabPanel>
                )
              }
              {
                uploadLinkedBtn && (
                  <TabPanel index={3}>
                    <UploadAccountLeads />
                  </TabPanel>
                )
              }
              <TabPanel index={4}>
                <UploadCompanyTrainingVideo />
              </TabPanel>
              <TabPanel index={5}>
                <UploadCompanyTrainingView />
              </TabPanel>
            </Tabs>
          </div>




          {showModal &&
            <div data-testid="adminModal">
              <Modal
                autoHeight
                show={showModal}
                handleClose={() => setShowModal(false)}
              >
                {modalDecider()}
              </Modal>
            </div>
          }
        </div>
      ) : accessState === 'LOADING' ? (
        <TableStatus status="LOADING" />
      ) : (
        <>
          <h1>Whoops!</h1>
          <h3>You don‘t have the right permissions to view this page.</h3>
        </>
      )}

      {showDeleteAccountModal && (
        <Warning
          warningMessage={
            deleteAccountLeadsError
              ? deleteAccountLeadsError
              : isDeletingAccountLeads
                ? 'Deleting "Enogen Account Leads" detail, please wait...'
                : 'This action will remove all associated "Enogen Account Leads" from the "Linked Contacts" section from every reseller company type. This cannot be undone.'
          }
          warningMessage2={
            !isDeletingAccountLeads ?
              'Please select "Confirm" to continue or "Cancel" to return to the main section.'
              : ' '
          }
          confirmationButtonText="Confirm"
          disabledButton={isDeletingAccountLeads}
          handleDismissWarning={handleDeleteAccountLeadsModel}
          handleSubmit={deleteAccountLeadsClicked}
        />
      )}

    </div>
  )
}

export default Admin
